import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Send, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(1, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
};

/**
 * PollCommentsThread — collapsible thread where directors add reasoning
 * before finalising a vote. Comments appear live to fellow board members
 * via WebSocket (poll.comment / poll.comment.delete on property:{pid}).
 */
export function PollCommentsThread({ poll, subscribe }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const listRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/board/polls/${poll.id}/comments`);
      setItems(data.items || []);
      setLoaded(true);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load comments");
    }
  }, [poll.id]);

  useEffect(() => {
    if (open && !loaded) load();
  }, [open, loaded, load]);

  // Live comment fan-out via property topic
  useEffect(() => {
    if (!subscribe || !poll.property_id) return;
    const unsub = subscribe(`property:${poll.property_id}`, ({ event, payload }) => {
      if (payload?.poll_id !== poll.id) return;
      if (event === "poll.comment") {
        setItems((prev) => (prev.find((c) => c.id === payload.comment.id) ? prev : [...prev, payload.comment]));
        setLoaded(true);
        if (open && listRef.current) {
          setTimeout(() => { listRef.current.scrollTop = listRef.current.scrollHeight; }, 40);
        }
      } else if (event === "poll.comment.delete") {
        setItems((prev) => prev.filter((c) => c.id !== payload.comment_id));
      }
    });
    return unsub;
  }, [subscribe, poll.property_id, poll.id, open]);

  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      await api.post(`/board/polls/${poll.id}/comments`, { text: t });
      setText("");
      // WebSocket fan-out will append it; no need to re-fetch
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to post");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/board/polls/${poll.id}/comments/${id}`);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Delete failed");
    }
  };

  const optionLabelById = useMemo(() => {
    const m = {};
    (poll.options || []).forEach((o) => { m[o.id] = o.label; });
    return m;
  }, [poll.options]);

  const count = items.length;

  return (
    <div className="mt-2 border-t border-white/5 pt-2">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-[11px] text-zinc-400 hover:text-zinc-200 transition"
        data-testid={`poll-comments-toggle-${poll.id}`}
      >
        <span className="inline-flex items-center gap-1.5">
          <MessageSquare className="h-3 w-3" />
          {count === 0 ? "Add reasoning" : `${count} note${count === 1 ? "" : "s"} from directors`}
        </span>
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>

      {open && (
        <div className="mt-2" data-testid={`poll-comments-thread-${poll.id}`}>
          {items.length > 0 && (
            <div ref={listRef} className="max-h-56 overflow-y-auto space-y-1.5 mb-2 pr-1">
              {items.map((c) => {
                const isMine = c.author_id === user?.id;
                const voted = c.voted_option_id ? optionLabelById[c.voted_option_id] : null;
                return (
                  <div key={c.id} className="rounded-md border border-white/5 bg-white/[0.02] px-2.5 py-1.5" data-testid={`poll-comment-${c.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 text-[11px] text-zinc-400 truncate">
                        <span className="text-zinc-200 font-medium">{c.author_name || c.author_email}</span>
                        {c.author_role && <span className="text-zinc-500"> · {c.author_role}</span>}
                        {voted && (
                          <Badge variant="outline" className="ml-1 border-emerald-500/30 text-emerald-300 text-[10px] h-4 px-1">
                            voted {voted}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="text-[10px] text-zinc-500 tabular-nums">{timeAgo(c.at)}</span>
                        {isMine && (
                          <button onClick={() => remove(c.id)} className="text-zinc-500 hover:text-red-400" data-testid={`poll-comment-delete-${c.id}`}>
                            <Trash2 className="h-2.5 w-2.5" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-zinc-200 whitespace-pre-wrap mt-0.5">{c.text}</div>
                  </div>
                );
              })}
            </div>
          )}

          {poll.status === "open" ? (
            <div className="flex items-end gap-2">
              <Textarea
                rows={2}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Share your reasoning so fellow directors can see it before finalising their vote…"
                className="bg-white/[0.04] border-white/10 text-xs min-h-[52px] flex-1"
                data-testid={`poll-comment-input-${poll.id}`}
              />
              <Button size="sm" onClick={submit} disabled={busy || !text.trim()}
                      className="bg-violet-500 hover:bg-violet-600 rounded-full h-8 self-end"
                      data-testid={`poll-comment-submit-${poll.id}`}>
                <Send className="h-3 w-3 mr-1" /> Post
              </Button>
            </div>
          ) : (
            <div className="text-[11px] text-zinc-500 italic">Poll is closed — comments read-only.</div>
          )}
        </div>
      )}
    </div>
  );
}

export default PollCommentsThread;
